<!-- EmployerOperatorManagement.vue -->
<template>
  <div class="body employer-operator-management">
    <Navbar :show-back-button="true" @back="navigateToDashboard"/>
    <div class="page-header" style="margin-top: 15px;">
      <div style="display: flex; gap: 15px;">
        <!-- <button class="back-arrow" @click="navigateToDashboard">
          →
        </button> -->
        <h2>{{ $t('systemAdmin.operatorManagement.title') }}</h2>
      </div>
      <div class="company-info" v-if="selectedCompany">
        <span class="company-name">{{ selectedCompany.company_name }}</span>
      </div>
    </div>

    <!-- Main Content -->
    <div v-if="selectedCompany" class="main-content">
      <!-- Tabs -->
      <div class="tabs">
        <button 
          class="tab-btn" 
          :class="{ 'active': activeTab === 'categories' }"
          @click="activeTab = 'categories'"
        >
          {{ $t('systemAdmin.operatorManagement.tab_categories') }}
        </button>
        <button 
          class="tab-btn" 
          :class="{ 'active': activeTab === 'operators' }"
          @click="activeTab = 'operators'"
        >
          {{ $t('systemAdmin.operatorManagement.tab_operators') }}
        </button>
        <button 
          class="tab-btn" 
          :class="{ 'active': activeTab === 'stats' }"
          @click="activeTab = 'stats'"
        >
          {{ $t('systemAdmin.operatorManagement.tab_stats') }}
        </button>
      </div>

      <!-- Categories Tab -->
      <div v-if="activeTab === 'categories'" class="tab-content">
        <div class="section-header">
          <h3>{{ $t('systemAdmin.operatorManagement.categories_title') }}</h3>
          <button @click="showCategoryModal = true" class="add-btn">
            + {{ $t('systemAdmin.operatorManagement.new_category') }}
          </button>
        </div>

        <div v-if="loading.categories" class="loading-state">
          <span>{{ $t('systemAdmin.operatorManagement.loading') }}</span>
        </div>

        <div v-else-if="categories.length > 0" class="categories-grid">
          <div 
            v-for="category in categories" 
            :key="category.id"
            class="category-card"
            :class="{ 'inactive': !category.is_active }"
          >
            <div class="category-header">
              <span class="category-name">{{ category.category_name }}</span>
              <div style="display: flex; gap: 8px; align-items: center;">
                <span class="category-type-badge" :class="category.category_type">
                  {{ getCategoryTypeName(category.category_type) }}
                </span>
                <span class="category-status" :class="{ 'active': category.is_active }">
                  {{ category.is_active ? $t('systemAdmin.operatorManagement.active') : $t('systemAdmin.operatorManagement.inactive') }}
                </span>
              </div>
            </div>
            <div class="category-desc">{{ category.description || $t('systemAdmin.operatorManagement.no_description') }}</div>
            <div class="category-meta">
              <span>{{ $t('systemAdmin.operatorManagement.created_at') }}: {{ formatDate(category.created_at) }}</span>
            </div>
            <div class="category-actions">
              <button @click="editCategory(category)" class="edit-btn">
                ✏️ {{ $t('systemAdmin.operatorManagement.edit') }}
              </button>
              <button 
                @click="toggleCategoryStatus(category)" 
                class="toggle-btn"
                :class="{ 'activate': !category.is_active }"
              >
                {{ category.is_active ? $t('systemAdmin.operatorManagement.deactivate') : $t('systemAdmin.operatorManagement.activate') }}
              </button>
            </div>
          </div>
        </div>

        <div v-else class="empty-state">
          <span class="empty-icon">📂</span>
          <span>{{ $t('systemAdmin.operatorManagement.no_categories') }}</span>
        </div>
      </div>

      <!-- Operators Tab -->
      <div v-if="activeTab === 'operators'" class="tab-content">
        <div class="section-header">
          <h3>{{ $t('systemAdmin.operatorManagement.operators_title') }}</h3>
          <div class="header-actions">
            <select v-model="operatorFilter" class="filter-select">
              <option value="all">{{ $t('systemAdmin.operatorManagement.filter_all') }}</option>
              <option value="active">{{ $t('systemAdmin.operatorManagement.filter_active') }}</option>
              <option value="inactive">{{ $t('systemAdmin.operatorManagement.filter_inactive') }}</option>
              <option v-for="cat in categories" :key="cat.id" :value="`category_${cat.id}`">
                {{ cat.category_name }}
              </option>
            </select>
            <button @click="showOperatorModalMethod" class="add-btn">
              + {{ $t('systemAdmin.operatorManagement.new_operator') }}
            </button>
          </div>
        </div>

        <div v-if="loading.operators" class="loading-state">
          <span>{{ $t('systemAdmin.operatorManagement.loading') }}</span>
        </div>

        <div v-else-if="filteredOperators.length > 0" class="operators-list">
          <div 
            v-for="operator in filteredOperators" 
            :key="operator.id"
            class="operator-card"
            :class="{ 'inactive': !operator.is_active }"
          >
            <div class="operator-header">
              <div class="operator-info">
                <span class="operator-name">{{ operator.operator_name }}</span>
                <span class="operator-username">({{ operator.username }})</span>
              </div>
              <span class="operator-status" :class="{ 'active': operator.is_active }">
                {{ operator.is_active ? $t('systemAdmin.operatorManagement.active') : $t('systemAdmin.operatorManagement.inactive') }}
              </span>
            </div>

            <div class="operator-details">
              <div class="detail-item" v-if="operator.phone_number">
                <span class="detail-label">{{ $t('systemAdmin.operatorManagement.phone') }}:</span>
                <span class="detail-value">{{ operator.phone_number }}</span>
              </div>
              <div class="detail-item" v-if="operator.email">
                <span class="detail-label">{{ $t('systemAdmin.operatorManagement.email') }}:</span>
                <span class="detail-value">{{ operator.email }}</span>
              </div>
            </div>

            <div class="operator-categories">
              <span 
                v-for="cat in operator.categories" 
                :key="cat.id"
                class="category-tag"
                :class="cat.name"
              >
                {{ cat.name }}
              </span>
              <span v-if="operator.categories.length === 0" class="no-categories">
                {{ $t('systemAdmin.operatorManagement.no_categories_assigned') }}
              </span>
            </div>

            <div class="operator-actions">
              <button @click="editOperator(operator)" class="edit-btn">
                ✏️ {{ $t('systemAdmin.operatorManagement.edit') }}
              </button>
              <button 
                @click="toggleOperatorStatus(operator)" 
                class="toggle-btn"
                :class="{ 'activate': !operator.is_active }"
              >
                {{ operator.is_active ? $t('systemAdmin.operatorManagement.deactivate') : $t('systemAdmin.operatorManagement.activate') }}
              </button>
            </div>
          </div>
        </div>

        <div v-else class="empty-state">
          <span class="empty-icon">👥</span>
          <span>{{ $t('systemAdmin.operatorManagement.no_operators') }}</span>
        </div>
      </div>

      <!-- Stats Tab -->
      <div v-if="activeTab === 'stats'" class="tab-content">
        <div class="stats-grid">
          <!-- Overall Stats -->
          <div class="stats-card">
            <h4>{{ $t('systemAdmin.operatorManagement.stats_overall') }}</h4>
            <div class="stat-item">
              <span>{{ $t('systemAdmin.operatorManagement.total_operators') }}:</span>
              <strong>{{ stats.total_operators || 0 }}</strong>
            </div>
            <div class="stat-item">
              <span>{{ $t('systemAdmin.operatorManagement.active_operators') }}:</span>
              <strong>{{ stats.active_operators || 0 }}</strong>
            </div>
            <div class="stat-item">
              <span>{{ $t('systemAdmin.operatorManagement.total_categories') }}:</span>
              <strong>{{ categories.length }}</strong>
            </div>
          </div>

          <!-- Category Stats -->
          <div class="stats-card">
            <h4>{{ $t('systemAdmin.operatorManagement.stats_categories') }}</h4>
            <div v-for="(catStats, catName) in stats.category_stats" :key="catName" class="stat-item">
              <span>{{ catName }}:</span>
              <strong>{{ catStats.total }} ({{ catStats.active }} {{ $t('systemAdmin.operatorManagement.active') }})</strong>
              <div class="stat-chat-info" v-if="catStats.active_chats !== undefined">
                <span class="chat-badge active">{{ catStats.active_chats }} {{ $t('systemAdmin.operatorManagement.active_chats') }}</span>
                <span class="chat-badge waiting">{{ catStats.waiting_chats || 0 }} {{ $t('systemAdmin.operatorManagement.waiting') }}</span>
              </div>
            </div>
          </div>

          <!-- Chat Stats -->
          <div class="stats-card">
            <h4>{{ $t('systemAdmin.operatorManagement.stats_chats') }}</h4>
            <div class="stat-item">
              <span>{{ $t('systemAdmin.operatorManagement.active_chats') }}:</span>
              <strong>{{ activeChats.length }}</strong>
            </div>
            <div class="stat-item">
              <span>{{ $t('systemAdmin.operatorManagement.waiting_response') }}:</span>
              <strong>{{ pendingQuestionsCount }}</strong>
            </div>
          </div>
        </div>

        <!-- Export Section -->
        <div class="export-section">
          <h4>{{ $t('systemAdmin.operatorManagement.reports') }}</h4>
          <div class="export-buttons">
            <button @click="exportOperators" class="export-btn">
              📥 {{ $t('systemAdmin.operatorManagement.export_operators') }}
            </button>
            <button @click="exportCategories" class="export-btn">
              📥 {{ $t('systemAdmin.operatorManagement.export_categories') }}
            </button>
            <button @click="exportChatStats" class="export-btn">
              📥 {{ $t('systemAdmin.operatorManagement.export_chat_stats') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Category Modal -->
    <div v-if="showCategoryModal" class="modal-overlay" @click="showCategoryModal = false">
      <div class="modal-content" @click.stop>
        <h3>{{ editingCategory ? $t('systemAdmin.operatorManagement.edit_category') : $t('systemAdmin.operatorManagement.new_category') }}</h3>
        
        <form @submit.prevent="saveCategory" class="modal-form">
          <div class="form-group">
            <label>{{ $t('systemAdmin.operatorManagement.category_name') }} *</label>
            <input 
              v-model="categoryForm.name"
              type="text"
              required
              :placeholder="$t('systemAdmin.operatorManagement.category_name_placeholder')"
            />
          </div>

          <div class="form-group">
            <label>{{ $t('systemAdmin.operatorManagement.category_type') }} *</label>
            <select v-model="categoryForm.type" class="category-type-select" required>
              <option value="">{{ $t('systemAdmin.operatorManagement.select_type') }}</option>
              <option value="support">{{ $t('systemAdmin.operatorManagement.type_support') }}</option>
              <option value="financial">{{ $t('systemAdmin.operatorManagement.type_financial') }}</option>
              <option value="commercial">{{ $t('systemAdmin.operatorManagement.type_commercial') }}</option>
              <option value="business">{{ $t('systemAdmin.operatorManagement.type_business') }}</option>
              <option value="technical">{{ $t('systemAdmin.operatorManagement.type_technical') }}</option>
              <option value="other">{{ $t('systemAdmin.operatorManagement.type_other') }}</option>
            </select>
          </div>

          <div class="form-group">
            <label>{{ $t('systemAdmin.operatorManagement.description') }}</label>
            <textarea 
              v-model="categoryForm.description"
              rows="3"
              :placeholder="$t('systemAdmin.operatorManagement.description_placeholder')"
            ></textarea>
          </div>

          <div class="form-group" v-if="editingCategory">
            <label>
              <input 
                v-model="categoryForm.is_active" 
                type="checkbox"
              />
              {{ $t('systemAdmin.operatorManagement.active') }}
            </label>
          </div>

          <div class="modal-actions">
            <button type="submit" class="save-btn" :disabled="saving">
              {{ saving ? $t('systemAdmin.operatorManagement.saving') : $t('systemAdmin.operatorManagement.save') }}
            </button>
            <button type="button" @click="showCategoryModal = false" class="cancel-btn">
              {{ $t('systemAdmin.operatorManagement.cancel') }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Operator Modal -->
    <div v-if="showOperatorModal" class="modal-overlay" @click="showOperatorModal = false">
      <div class="modal-content" @click.stop>
        <h3>{{ editingOperator ? $t('systemAdmin.operatorManagement.edit_operator') : $t('systemAdmin.operatorManagement.new_operator') }}</h3>
        
        <form @submit.prevent="saveOperator" class="modal-form">
          <div class="form-group">
            <label>{{ $t('systemAdmin.operatorManagement.search_customer') }}</label>
            <div class="customer-search-box">
              <input 
                v-model="customerSearchTerm" 
                type="text" 
                :placeholder="$t('systemAdmin.operatorManagement.search_placeholder')"
                @keyup.enter="searchCustomers"
                :readonly="editingOperator"
              />
              <button @click="searchCustomers" type="button" :disabled="searching">
                {{ searching ? '...' : $t('systemAdmin.operatorManagement.search') }}
              </button>
            </div>
            
            <div v-if="customerSearchResults.length > 0" class="customer-search-results">
              <div 
                v-for="customer in customerSearchResults" 
                :key="customer.id_subcode"
                class="customer-result-item"
                :class="{ 'disabled': customer.is_already_operator }"
                @click="!customer.is_already_operator && selectCustomer(customer)"
              >
                <div class="customer-info">
                  <div class="customer-name">{{ customer.name }}</div>
                  <div class="customer-details">
                    <span v-if="customer.phone">📞 {{ customer.phone }}</span>
                    <span v-if="customer.mobile">📱 {{ customer.mobile }}</span>
                  </div>
                </div>
                <div class="customer-status">
                  <span v-if="customer.is_already_operator" class="status-badge operator">
                    {{ customer.operator_status === 'active' ? $t('systemAdmin.operatorManagement.active_operator') : $t('systemAdmin.operatorManagement.inactive_operator') }}
                  </span>
                </div>
              </div>
            </div>
            
            <div v-else-if="searched && !searching" class="no-results">
              {{ $t('systemAdmin.operatorManagement.no_customers_found') }}
            </div>
          </div>

          <div class="form-group">
            <label>{{ $t('systemAdmin.operatorManagement.operator_name') }} *</label>
            <input 
              v-model="operatorForm.operator_name"
              type="text"
              required
              :placeholder="$t('systemAdmin.operatorManagement.operator_name_placeholder')"
              readonly
            />
          </div>

          <div class="form-group">
            <label>{{ $t('systemAdmin.operatorManagement.username') }} *</label>
            <input 
              v-model="operatorForm.username"
              type="text"
              required
              :placeholder="$t('systemAdmin.operatorManagement.username_placeholder')"
              readonly
            />
          </div>

          <div class="form-group">
            <label>{{ $t('systemAdmin.operatorManagement.operator_id_label') }}</label>
            <input 
              v-model="operatorForm.operator_id"
              type="text"
              :placeholder="editingOperator ? $t('systemAdmin.operatorManagement.current_id') : $t('systemAdmin.operatorManagement.auto_generated')"
              :readonly="!editingOperator"
            />
          </div>

          <div class="form-row">
            <div class="form-group half">
              <label>{{ $t('systemAdmin.operatorManagement.phone') }}</label>
              <input 
                v-model="operatorForm.phone_number"
                type="tel"
                :placeholder="$t('systemAdmin.operatorManagement.phone_placeholder')"
                readonly
              />
            </div>

            <div class="form-group half">
              <label>{{ $t('systemAdmin.operatorManagement.email') }}</label>
              <input 
                v-model="operatorForm.email"
                type="email"
                placeholder="example@domain.com"
                readonly
              />
            </div>
          </div>

          <div class="form-group">
            <label>{{ $t('systemAdmin.operatorManagement.categories') }}</label>
            <div class="categories-checkbox-group">
              <div 
                v-for="category in categories" 
                :key="category.id"
                class="category-checkbox"
              >
                <label>
                  <input 
                    type="checkbox"
                    :value="category.id"
                    v-model="operatorForm.categories"
                  />
                  {{ category.category_name }}
                </label>
              </div>
            </div>
          </div>

          <div class="form-group" v-if="editingOperator">
            <label>
              <input 
                v-model="operatorForm.is_active" 
                type="checkbox"
              />
              {{ $t('systemAdmin.operatorManagement.active') }}
            </label>
          </div>

          <div class="modal-actions">
            <button type="submit" class="save-btn" :disabled="saving">
              {{ saving ? $t('systemAdmin.operatorManagement.saving') : $t('systemAdmin.operatorManagement.save') }}
            </button>
            <button type="button" @click="showOperatorModal = false" class="cancel-btn">
              {{ $t('systemAdmin.operatorManagement.cancel') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { employerService } from '@/services/api'
import Navbar from '@/components/Navbar.vue';
import { useRouter } from 'vue-router'

export default {
  name: 'EmployerOperatorManagement',
  components: {
    Navbar
  },
  setup() {
    const authStore = useAuthStore()
    
    // Get selected company from auth store
    const selectedCompany = computed(() => authStore.selectedCompany)
    
    // State
    const activeTab = ref('categories')
    const operatorFilter = ref('all')
    
    const categories = ref([])
    const operators = ref([])
    const activeChats = ref([])
    const pendingQuestionsCount = ref(0)
    const stats = ref({})
    const router = useRouter()
    
    // Loading states
    const loading = ref({
      categories: false,
      operators: false,
      stats: false
    })
    
    // Modal states
    const showCategoryModal = ref(false)
    const showOperatorModal = ref(false)
    const editingCategory = ref(null)
    const editingOperator = ref(null)
    const saving = ref(false)
    
    // Forms
    const categoryForm = ref({
      name: '',
      type: '',  
      description: '',
      is_active: true
    })
    
    const operatorForm = ref({
      operator_id: '',
      operator_name: '',
      username: '',
      phone_number: '',
      email: '',
      categories: [],
      is_active: true
    })

    // Customer search
    const customerSearchTerm = ref('')
    const searching = ref(false)
    const searched = ref(false)
    const customerSearchResults = ref([])

    const searchCustomers = async () => {
      if (!customerSearchTerm.value || customerSearchTerm.value.length < 2) {
        alert('حداقل ۲ کاراکتر وارد کنید')
        return
      }
      
      searching.value = true
      searched.value = true
      customerSearchResults.value = [] // Reset results
      
      try {
        console.log('Searching for:', customerSearchTerm.value)
        const response = await employerService.searchCustomerForOperator({
          search_term: customerSearchTerm.value
        })
        
        console.log('Search response:', response)
        
        // Handle response structure
        if (response.data) {
          // Axios wraps response in data
          if (response.data.success) {
            customerSearchResults.value = response.data.customers || []
          } else {
            alert(response.data.message || 'خطا در جستجو')
          }
        } else if (response.success) {
          // Direct response
          customerSearchResults.value = response.customers || []
        } else {
          alert(response.message || 'خطا در جستجو')
        }
        
        console.log('Search results:', customerSearchResults.value)
      } catch (error) {
        console.error('Search error:', error)
        customerSearchResults.value = []
        alert('خطا در جستجوی مشتریان: ' + (error.response?.data?.detail || error.message))
      } finally {
        searching.value = false
      }
    }
    
    const navigateToDashboard = () => {
      router.push('/admin/dashboard')
    }

    const selectCustomer = (customer) => {
      console.log('Selected customer:', customer)
      // Auto-fill the operator form with customer data
      operatorForm.value.operator_id = String(customer.id_subcode)
      operatorForm.value.operator_name = customer.name
      operatorForm.value.phone_number = customer.phone || customer.mobile || ''
      operatorForm.value.email = customer.email || ''
      
      // Generate username if empty
      if (!operatorForm.value.username) {
        const baseName = customer.name.replace(/\s+/g, '_').toLowerCase()
        operatorForm.value.username = `${baseName}_${customer.id_subcode}`
      }
      
      // Clear search results
      customerSearchResults.value = []
      customerSearchTerm.value = ''
      searched.value = false
    }
    
    // Computed
    const filteredOperators = computed(() => {
      if (!operators.value) return []
      if (operatorFilter.value === 'all') return operators.value
      if (operatorFilter.value === 'active') return operators.value.filter(op => op.is_active)
      if (operatorFilter.value === 'inactive') return operators.value.filter(op => !op.is_active)
      
      if (operatorFilter.value.startsWith('category_')) {
        const categoryId = parseInt(operatorFilter.value.replace('category_', ''))
        return operators.value.filter(op => 
          op.categories && op.categories.some(cat => cat.id === categoryId)
        )
      }
      
      return operators.value
    })

    // Computed for search results (with null check)
    const hasSearchResults = computed(() => {
      return customerSearchResults.value && customerSearchResults.value.length > 0
    })
    
    // Methods
    const loadCompanyData = async () => {
      if (!selectedCompany.value) return
      
      console.log('Loading data for company:', selectedCompany.value.company_id)
      await Promise.all([
        loadCategories(),
        loadOperators(),
        loadStats()
      ])
    }
    
    const loadCategories = async () => {
      try {
        loading.value.categories = true
        const response = await employerService.getOperatorCategories()
        console.log('Categories response:', response)
        
        if (response.data && response.data.success) {
          categories.value = response.data.categories || []
        } else if (response.success) {
          categories.value = response.categories || []
        }
        console.log('categories.value ', categories.value)
      } catch (error) {
        console.error('Error loading categories:', error)
        categories.value = []
      } finally {
        loading.value.categories = false
      }
    }
    
    const loadOperators = async () => {
      try {
        loading.value.operators = true
        const response = await employerService.getCompanyOperators()
        console.log('Operators response:', response)
        
        if (response.data && response.data.success) {
          operators.value = response.data.operators || []
        } else if (response.success) {
          operators.value = response.operators || []
        }
      } catch (error) {
        console.error('Error loading operators:', error)
        operators.value = []
      } finally {
        loading.value.operators = false
      }
    }
    
    const loadStats = async () => {
      try {
        loading.value.stats = true
        const response = await employerService.getOperatorStats()
        console.log('Stats response:', response)
        
        if (response.data && response.data.success) {
          stats.value = response.data.stats || {}
        } else if (response.success) {
          stats.value = response.stats || {}
        }
        
        // Also load active chats count
        const chatsData = await employerService.getActiveChats()
        if (chatsData.data && chatsData.data.success) {
          activeChats.value = chatsData.data.active_chats || []
        } else if (chatsData.success) {
          activeChats.value = chatsData.active_chats || []
        }
        
        // Load pending questions count
        const pendingData = await employerService.getPendingQuestions()
        if (pendingData.data && pendingData.data.success) {
          pendingQuestionsCount.value = pendingData.data.count || 0
        } else if (pendingData.success) {
          pendingQuestionsCount.value = pendingData.count || 0
        }
      } catch (error) {
        console.error('Error loading stats:', error)
        stats.value = {}
      } finally {
        loading.value.stats = false
      }
    }
    
    // Category methods
    const saveCategory = async () => {
      if (!categoryForm.value.name || !categoryForm.value.type) {
        alert('لطفاً نام و نوع دسته‌بندی را وارد کنید')
        return
      }
      
      saving.value = true
      
      try {
        if (editingCategory.value) {
          // Update category
          const data = await employerService.updateOperatorCategory(
            editingCategory.value.id,
            {
              company_id: selectedCompany.value.company_id,
              category_name: categoryForm.value.name,
              category_type: categoryForm.value.type,
              description: categoryForm.value.description,
              is_active: categoryForm.value.is_active
            }
          )
          if (data.data?.success || data.success) {
            alert('دسته‌بندی با موفقیت ویرایش شد')
          }
        } else {
          // Create category
          const data = await employerService.createOperatorCategory({
            company_id: selectedCompany.value.company_id,
            category_name: categoryForm.value.name,
            category_type: categoryForm.value.type,
            description: categoryForm.value.description,
            is_active: true
          })
          if (data.data?.success || data.success) {
            alert('دسته‌بندی با موفقیت ایجاد شد')
          }
        }
        
        showCategoryModal.value = false
        resetCategoryForm()
        await loadCategories()
      } catch (error) {
        console.error('Error saving category:', error)
        alert('خطا در ذخیره دسته‌بندی: ' + (error.response?.data?.detail || error.message))
      } finally {
        saving.value = false
      }
    }
    
    const editCategory = (category) => {
      editingCategory.value = category
      categoryForm.value = {
        name: category.category_name,
        type: category.category_type,
        description: category.description || '',
        is_active: category.is_active
      }
      showCategoryModal.value = true
    }
    
    const toggleCategoryStatus = async (category) => {
      const newStatus = !category.is_active
      const action = newStatus ? 'فعال' : 'غیرفعال'
      
      if (!confirm(`آیا از ${action} کردن این دسته‌بندی اطمینان دارید؟`)) {
        return
      }
      
      try {
        const data = await employerService.updateOperatorCategory(category.id, {
          is_active: newStatus
        })
        
        if (data.data?.success || data.success) {
          alert(`دسته‌بندی با موفقیت ${action} شد`)
          await loadCategories()
        }
      } catch (error) {
        console.error('Error toggling category status:', error)
        alert('خطا در تغییر وضعیت دسته‌بندی')
      }
    }
    
    const resetCategoryForm = () => {
      editingCategory.value = null
      categoryForm.value = {
        name: '',
        type: '',
        description: '',
        is_active: true
      }
    }
    
    // Operator methods
    const saveOperator = async () => {
      if (!operatorForm.value.operator_name || !operatorForm.value.username) {
        alert('لطفاً نام و نام کاربری اپراتور را وارد کنید')
        return
      }
      
      saving.value = true
      
      try {
        if (editingOperator.value) {
          console.log('editingOperator ', editingOperator.value)
          // Update operator
          const data = await employerService.updateCompanyOperator(
            editingOperator.value.operator_id,
            {
              ...operatorForm.value,
              company_id: selectedCompany.value.company_id
            }
          )
          if (data.data?.success || data.success) {
            alert('اپراتور با موفقیت ویرایش شد')
          }
        } else {
          // Create operator
          const data = await employerService.registerCompanyOperator({
            company_id: selectedCompany.value.company_id,
            operator_id: operatorForm.value.operator_id || `op_${Date.now()}`,
            operator_name: operatorForm.value.operator_name,
            username: operatorForm.value.username,
            phone_number: operatorForm.value.phone_number,
            email: operatorForm.value.email,
            categories: operatorForm.value.categories,
            is_active: true
          })
          if (data.data?.success || data.success) {
            alert('اپراتور با موفقیت ایجاد شد')
          }
        }
        
        showOperatorModal.value = false
        resetOperatorForm()
        await loadOperators()
      } catch (error) {
        console.error('Error saving operator:', error)
        alert('خطا در ذخیره اپراتور: ' + (error.response?.data?.detail || error.message))
      } finally {
        saving.value = false
      }
    }
    
    const editOperator = (operator) => {
      editingOperator.value = operator
      operatorForm.value = {
        operator_id: operator.operator_id,
        operator_name: operator.operator_name,
        username: operator.username,
        phone_number: operator.phone_number || '',
        email: operator.email || '',
        categories: operator.categories ? operator.categories.map(c => c.id) : [],
        is_active: operator.is_active
      }
      showOperatorModal.value = true
      customerSearchTerm.value = ''
    }
    
    const toggleOperatorStatus = async (operator) => {
      const newStatus = !operator.is_active
      const action = newStatus ? 'فعال' : 'غیرفعال'
      
      if (!confirm(`آیا از ${action} کردن این اپراتور اطمینان دارید؟`)) {
        return
      }
      
      try {
        const data = await employerService.updateCompanyOperator(operator.operator_id, {
          is_active: newStatus
        })
        
        if (data.data?.success || data.success) {
          alert(`اپراتور با موفقیت ${action} شد`)
          await loadOperators()
        }
      } catch (error) {
        console.error('Error toggling operator status:', error)
        alert('خطا در تغییر وضعیت اپراتور')
      }
    }
    
    const syncWithMessenger = async (operator) => {
      if (!confirm('آیا از همگام‌سازی این اپراتور با سیستم پیام‌رسان اطمینان دارید؟')) {
        return
      }
      
      try {
        const data = await employerService.syncOperatorWithMessenger(operator.operator_id)
        
        if (data.data?.success || data.success) {
          alert('اپراتور با موفقیت با سیستم پیام‌رسان همگام‌سازی شد')
        } else {
          alert('خطا در همگام‌سازی: ' + (data.message || 'خطای ناشناخته'))
        }
      } catch (error) {
        console.error('Error syncing operator:', error)
        alert('خطا در همگام‌سازی با سیستم پیام‌رسان')
      }
    }
    
    const resetOperatorForm = () => {
      editingOperator.value = null
      operatorForm.value = {
        operator_id: '',
        operator_name: '',
        username: '',
        phone_number: '',
        email: '',
        categories: [],
        is_active: true
      }
      customerSearchTerm.value = ''
      customerSearchResults.value = []
      searched.value = false
    }
    
    const showOperatorModalMethod = () => {
      showOperatorModal.value = true
      resetOperatorForm()
    }

    // Export methods
    const exportOperators = () => {
      const data = operators.value.map(op => ({
        'نام': op.operator_name,
        'نام کاربری': op.username,
        'تلفن': op.phone_number || '-',
        'ایمیل': op.email || '-',
        'دسته‌بندی‌ها': op.categories ? op.categories.map(c => c.name).join('، ') : '-',
        'وضعیت': op.is_active ? 'فعال' : 'غیرفعال'
      }))
      
      downloadCSV(data, 'operators_export.csv')
    }
    
    const exportCategories = () => {
      const data = categories.value.map(cat => ({
        'نام': cat.category_name,
        'توضیحات': cat.description || '-',
        'نوع': getCategoryTypeName(cat.category_type),
        'وضعیت': cat.is_active ? 'فعال' : 'غیرفعال',
        'تاریخ ایجاد': formatDate(cat.created_at)
      }))
      
      downloadCSV(data, 'categories_export.csv')
    }
    
    const exportChatStats = () => {
      const data = [{
        'کل اپراتورها': stats.value.total_operators || 0,
        'اپراتورهای فعال': stats.value.active_operators || 0,
        'چت‌های فعال': activeChats.value.length,
        'در انتظار پاسخ': pendingQuestionsCount.value,
        'تاریخ گزارش': new Date().toLocaleDateString('fa-IR')
      }]
      
      downloadCSV(data, 'chat_stats_export.csv')
    }
    
    const downloadCSV = (data, filename) => {
      if (!data || data.length === 0) {
        alert('داده‌ای برای خروجی وجود ندارد')
        return
      }
      
      const headers = Object.keys(data[0])
      const csvContent = [
        headers.join(','),
        ...data.map(row => headers.map(key => `"${row[key] || ''}"`).join(','))
      ].join('\n')
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = filename
      link.click()
      URL.revokeObjectURL(link.href)
    }
    
    // Helper methods
    const formatDate = (dateStr) => {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleDateString('fa-IR')
    }

    const getCategoryTypeName = (type) => {
      const types = {
        'support': 'پشتیبانی',
        'financial': 'مالی',
        'commercial': 'بازرگانی',
        'business': 'کسب و کار',
        'technical': 'فنی',
        'other': 'سایر'
      }
      return types[type] || type
    }
    
    // Lifecycle
    onMounted(() => {
      loadCompanyData()
    })
    
    return {
      selectedCompany,
      activeTab,
      operatorFilter,
      categories,
      operators,
      activeChats,
      pendingQuestionsCount,
      stats,
      loading,
      showCategoryModal,
      showOperatorModal,
      categoryForm,
      operatorForm,
      editingCategory,
      editingOperator,
      saving,
      filteredOperators,
      saveCategory,
      editCategory,
      toggleCategoryStatus,
      saveOperator,
      editOperator,
      toggleOperatorStatus,
      syncWithMessenger,
      exportOperators,
      exportCategories,
      exportChatStats,
      formatDate,
      customerSearchTerm,
      searching,
      searched,
      customerSearchResults,
      searchCustomers,
      selectCustomer,
      hasSearchResults,
      showOperatorModalMethod,
      navigateToDashboard,
      getCategoryTypeName
    }
  }
}
</script>

<style scoped>
.employer-operator-management {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  direction: rtl;
  background: var(--bg);
  color: var(--text);
}

/* Page Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 15px;
  border-bottom: 2px solid var(--card-border);
}

.page-header h2 {
  font-size: 24px;
  color: var(--heading);
  margin: 0;
}

.company-info {
  display: flex;
  align-items: center;
  gap: 15px;
  background: var(--bg);
  padding: 8px 15px;
  border-radius: 8px;
  border: 1px solid var(--card-border);
}

.company-name {
  font-weight: bold;
  color: var(--heading);
}

.change-company-btn {
  padding: 5px 10px;
  background: var(--muted);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: background 0.3s;
}

.change-company-btn:hover {
  background: var(--muted-2);
}

/* Tabs */
.tabs {
  display: flex;
  gap: 10px;
  margin-bottom: 25px;
  border-bottom: 2px solid var(--card-border);
  padding-bottom: 10px;
}

.tab-btn {
  padding: 10px 20px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  color: var(--subtext);
  position: relative;
  transition: color 0.3s;
}

.tab-btn:hover {
  color: var(--primary);
}

.tab-btn.active {
  color: var(--primary);
  font-weight: bold;
}

.tab-btn.active::after {
  content: '';
  position: absolute;
  bottom: -12px;
  left: 0;
  right: 0;
  height: 3px;
  background: var(--primary);
}

/* Section Headers */
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.section-header h3 {
  font-size: 18px;
  color: var(--heading);
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 15px;
  align-items: center;
}

.filter-select {
  padding: 8px 12px;
  border: 1px solid var(--card-border);
  border-radius: 4px;
  font-size: 14px;
  min-width: 150px;
  background: var(--card-bg);
  color: var(--text);
}

.add-btn {
  padding: 10px 20px;
  background: var(--green);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: background 0.3s;
}

.add-btn:hover {
  background: #218838;
}

/* Loading State */
.loading-state {
  text-align: center;
  padding: 40px;
  color: var(--subtext);
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: var(--subtext);
}

.empty-icon {
  font-size: 48px;
  display: block;
  margin-bottom: 15px;
  color: var(--muted);
}

/* Categories Grid */
.categories-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}

.category-card {
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px var(--shadow-1);
  transition: transform 0.2s, box-shadow 0.2s;
}

.category-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px var(--shadow-2);
}

.category-card.inactive {
  background: var(--bg);
  opacity: 0.8;
}

.category-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.category-name {
  font-size: 16px;
  font-weight: bold;
  color: var(--heading);
}

.category-status {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  background: var(--orange-1);
  color: white;
}

.category-status.active {
  background: var(--green);
}

.category-desc {
  color: var(--subtext);
  font-size: 14px;
  margin-bottom: 15px;
  min-height: 40px;
}

.category-meta {
  color: var(--muted);
  font-size: 12px;
  margin-bottom: 15px;
}

.category-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

/* Operators List */
.operators-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.operator-card {
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px var(--shadow-1);
}

.operator-card.inactive {
  background: var(--bg);
  opacity: 0.8;
}

.operator-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.operator-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.operator-name {
  font-size: 16px;
  font-weight: bold;
  color: var(--heading);
}

.operator-username {
  color: var(--subtext);
  font-size: 14px;
}

.operator-status {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  background: var(--orange-1);
  color: white;
}

.operator-status.active {
  background: var(--green);
}

.operator-details {
  display: flex;
  gap: 20px;
  margin-bottom: 15px;
}

.detail-item {
  display: flex;
  gap: 5px;
}

.detail-label {
  color: var(--subtext);
  font-size: 14px;
}

.detail-value {
  color: var(--text);
  font-size: 14px;
}

.operator-categories {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 15px;
}

.category-tag {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  background: rgba(59, 130, 246, 0.1);
  color: var(--primary);
}

.category-tag.financial {
  background: rgba(16, 185, 129, 0.1);
  color: var(--green);
}

.category-tag.commercial {
  background: rgba(249, 115, 22, 0.1);
  color: var(--orange-1);
}

.category-tag.customer {
  background: rgba(59, 130, 246, 0.1);
  color: var(--primary);
}

.category-tag.technical {
  background: rgba(139, 92, 246, 0.1);
  color: var(--gradient-2);
}

.no-categories {
  color: var(--muted);
  font-size: 12px;
}

.operator-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

/* Stats Tab */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.stats-card {
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px var(--shadow-1);
}

.stats-card h4 {
  margin: 0 0 20px 0;
  color: var(--heading);
  font-size: 16px;
  border-bottom: 1px solid var(--card-border);
  padding-bottom: 10px;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding: 8px 0;
  border-bottom: 1px dashed var(--card-border);
}

.stat-item:last-child {
  border-bottom: none;
}

.stat-chat-info {
  display: flex;
  gap: 5px;
}

.chat-badge {
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
}

.chat-badge.active {
  background: var(--green);
  color: white;
}

.chat-badge.waiting {
  background: #ffc107;
  color: #212529;
}

.export-section {
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 8px;
  padding: 20px;
}

.export-section h4 {
  margin: 0 0 15px 0;
  color: var(--heading);
  font-size: 16px;
}

.export-buttons {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.export-btn {
  padding: 10px 20px;
  background: #17a2b8;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: background 0.3s;
}

.export-btn:hover {
  background: #138496;
}

/* Buttons */
.edit-btn, .toggle-btn, .sync-btn {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.3s;
}

.edit-btn {
  background: var(--primary);
  color: white;
}

.edit-btn:hover {
  background: var(--primary-hover);
}

.toggle-btn {
  background: var(--orange-1);
  color: white;
}

.toggle-btn.activate {
  background: var(--green);
}

.toggle-btn:hover {
  opacity: 0.9;
}

.sync-btn {
  background: var(--gradient-2);
  color: white;
}

.sync-btn:hover {
  background: #5a32a3;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--modal-overlay);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99999;
}

.modal-content {
  background: var(--card-bg);
  border-radius: 8px;
  padding: 30px;
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px var(--shadow-2);
  border: 1px solid var(--card-border);
}

.modal-content h3 {
  margin: 0 0 20px 0;
  color: var(--heading);
  font-size: 18px;
}

/* Company Selector */
.companies-list {
  margin-bottom: 20px;
  max-height: 400px;
  overflow-y: auto;
}

.company-item {
  padding: 15px;
  border: 1px solid var(--card-border);
  border-radius: 4px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: all 0.3s;
  background: var(--bg);
}

.company-item:hover {
  background: var(--hover);
  border-color: var(--primary);
}

.company-item.selected {
  background: var(--ring);
  border-color: var(--primary);
}

.company-name {
  font-weight: bold;
  color: var(--heading);
  margin-bottom: 5px;
}

.company-desc {
  color: var(--subtext);
  font-size: 13px;
}

.close-modal-btn {
  padding: 10px 20px;
  background: var(--muted);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  width: 100%;
  font-size: 14px;
}

.close-modal-btn:hover {
  background: var(--muted-2);
}

/* Modal Forms */
.modal-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-size: 14px;
  color: var(--heading);
  font-weight: bold;
}

.form-group input[type="text"],
.form-group input[type="tel"],
.form-group input[type="email"],
.form-group textarea,
.form-group select {
  padding: 10px;
  border: 1px solid var(--card-border);
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.3s;
  background: var(--bg);
  color: var(--text);
}

.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 2px var(--ring);
}

.form-group input[readonly] {
  background: var(--bg);
  opacity: 0.7;
  cursor: not-allowed;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
}

.form-group.half {
  flex: 1;
}

.categories-checkbox-group {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px;
  padding: 10px;
  border: 1px solid var(--card-border);
  border-radius: 4px;
  max-height: 200px;
  overflow-y: auto;
  background: var(--bg);
}

.category-checkbox {
  display: flex;
  align-items: center;
}

.category-checkbox label {
  display: flex;
  align-items: center;
  gap: 5px;
  font-weight: normal;
  cursor: pointer;
  color: var(--text);
}

.category-checkbox input[type="checkbox"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.modal-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
}

.save-btn {
  padding: 10px 30px;
  background: var(--green);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.3s;
}

.save-btn:hover:not(:disabled) {
  background: #218838;
}

.save-btn:disabled {
  background: var(--muted);
  cursor: not-allowed;
  opacity: 0.6;
}

.cancel-btn {
  padding: 10px 30px;
  background: var(--orange-1);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.3s;
}

.cancel-btn:hover {
  background: #c82333;
}

/* Customer Search Box */
.customer-search-box {
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
}

.customer-search-box input {
  flex: 1;
  padding: 12px 15px;
  border: 2px solid var(--card-border);
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s;
  background: var(--bg);
  color: var(--text);
}

.customer-search-box input:focus {
  border-color: var(--primary);
  background: var(--card-bg);
  box-shadow: 0 0 0 3px var(--ring);
  outline: none;
}

.customer-search-box input::placeholder {
  color: var(--muted);
  font-size: 13px;
}

.customer-search-box button {
  padding: 0 25px;
  background: var(--primary);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s;
  white-space: nowrap;
  min-width: 100px;
}

.customer-search-box button:hover:not(:disabled) {
  background: var(--primary-hover);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px var(--ring);
}

.customer-search-box button:disabled {
  background: var(--muted);
  cursor: not-allowed;
  opacity: 0.6;
}

/* Customer Search Results */
.customer-search-results {
  margin-top: 15px;
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid var(--card-border);
  border-radius: 8px;
  background: var(--card-bg);
  box-shadow: 0 4px 12px var(--shadow-1);
}

.customer-result-item {
  padding: 15px;
  border-bottom: 1px solid var(--card-border);
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.customer-result-item:last-child {
  border-bottom: none;
}

.customer-result-item:hover:not(.disabled) {
  background: var(--ring);
  transform: translateX(-2px);
  border-right: 3px solid var(--primary);
}

.customer-result-item.disabled {
  background: var(--bg);
  opacity: 0.7;
  cursor: not-allowed;
}

.customer-info {
  flex: 1;
}

.customer-name {
  font-weight: bold;
  color: var(--heading);
  margin-bottom: 5px;
  font-size: 15px;
}

.customer-details {
  display: flex;
  gap: 15px;
  font-size: 13px;
  color: var(--subtext);
}

.customer-details span {
  display: flex;
  align-items: center;
  gap: 5px;
}

.customer-details span svg {
  font-size: 14px;
}

.customer-status {
  margin-right: 15px;
}

.status-badge {
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.status-badge.operator {
  background: rgba(59, 130, 246, 0.1);
  color: var(--primary);
  border: 1px solid var(--primary);
}

.status-badge.active {
  background: rgba(16, 185, 129, 0.1);
  color: var(--green);
  border: 1px solid var(--green);
}

.status-badge.inactive {
  background: rgba(249, 115, 22, 0.1);
  color: var(--orange-1);
  border: 1px solid var(--orange-1);
}

/* No Results */
.no-results {
  text-align: center;
  padding: 30px;
  color: var(--subtext);
  font-size: 14px;
  background: var(--bg);
  border-radius: 8px;
  margin-top: 15px;
  border: 1px dashed var(--card-border);
}

/* Back Arrow */
.back-arrow {
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  font-size: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: var(--primary);
  color: white;
}

.back-arrow:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 20px var(--ring);
}

/* Category Type Badge */
.category-type-select {
  padding: 10px;
  border: 1px solid var(--card-border);
  border-radius: 4px;
  font-size: 14px;
  background-color: var(--card-bg);
  color: var(--text);
  cursor: pointer;
}

.category-type-select:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 2px var(--ring);
}

.category-type-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: bold;
  margin-left: 8px;
}

.category-type-badge.support {
  background-color: rgba(59, 130, 246, 0.1);
  color: var(--primary);
}

.category-type-badge.financial {
  background-color: rgba(16, 185, 129, 0.1);
  color: var(--green);
}

.category-type-badge.commercial {
  background-color: rgba(249, 115, 22, 0.1);
  color: var(--orange-1);
}

.category-type-badge.business {
  background-color: rgba(59, 130, 246, 0.1);
  color: var(--primary);
}

.category-type-badge.technical {
  background-color: rgba(139, 92, 246, 0.1);
  color: var(--gradient-2);
}

.category-type-badge.other {
  background-color: rgba(108, 117, 125, 0.1);
  color: var(--muted);
}

/* Responsive Design */
@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    gap: 15px;
    text-align: center;
  }

  .tabs {
    flex-wrap: wrap;
  }

  .tab-btn {
    flex: 1;
    padding: 8px;
    font-size: 14px;
  }

  .section-header {
    flex-direction: column;
    gap: 15px;
  }

  .header-actions {
    width: 100%;
    flex-direction: column;
  }

  .filter-select {
    width: 100%;
  }

  .add-btn {
    width: 100%;
  }

  .categories-grid {
    grid-template-columns: 1fr;
  }

  .operator-details {
    flex-direction: column;
    gap: 5px;
  }

  .operator-actions {
    flex-direction: column;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .export-buttons {
    flex-direction: column;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .modal-content {
    padding: 20px;
    width: 95%;
  }
  
  .customer-search-box {
    flex-direction: column;
  }
  
  .customer-search-box button {
    width: 100%;
    padding: 12px;
  }
  
  .customer-details {
    flex-direction: column;
    gap: 5px;
  }
  
  .customer-result-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  .customer-status {
    margin-right: 0;
    width: 100%;
  }
  
  .status-badge {
    display: inline-block;
    width: 100%;
    text-align: center;
  }
}

/* RTL Specific */
.modal-actions {
  flex-direction: row-reverse;
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-content {
  animation: fadeIn 0.3s ease-out;
}

.category-card,
.operator-card {
  animation: fadeIn 0.3s ease-out;
}
</style>